import React, { useMemo } from "react";
import CrownIcon from "../icons/CrownIcon";
import GoalieIcon from "../icons/GoalieIcon";

// Stateless presentational pieces hoisted so React keeps their identity stable between renders
const NameTag = React.memo(({ player }) => {
  if (!player) return <span className="opacity-0">&nbsp;</span>; // placeholder keeps grid alignment
  const name =
    `${player.Player?.firstName} ${player.Player?.lastName} #${player.jersey ?? ""}`.trim();
  return <span className="whitespace-nowrap font-header">{name}</span>;
});

const Avatar = React.memo(
  ({ player }) =>
    player ? (
      <div className="h-8 w-8 overflow-hidden rounded-full">
        {player.Player?.logo && (
          <img
            className="h-8 w-8 object-cover"
            src={player.Player?.logo}
            alt={`${player.Player?.firstName} ${player.Player?.lastName}`}
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        )}
      </div>
    ) : (
      <div className="h-8 w-8 opacity-0" />
    ),
  (prev, next) => {
    // Only re-render when the actual image URL changes
    const prevSrc = prev.player ? prev.player.Player?.logo : null;
    const nextSrc = next.player ? next.player.Player?.logo : null;
    return prevSrc === nextSrc;
  },
);

const SpecialPlayerRow = ({
  homePlayer,
  awayPlayer,
  icon,
  elementClassName,
  title,
}) => (
  <>
    <div className={`col-start-1 ${elementClassName}`}>
      <Avatar player={homePlayer} />
    </div>
    <div className={`col-start-2 ${elementClassName}`}>
      <NameTag player={homePlayer} />
    </div>
    <div
      className={`col-start-3 mb-1 ml-1 flex items-center justify-center ${elementClassName}`}
    >
      {icon}
    </div>
    <div
      className={`col-start-4 mt-0.5 font-header font-bold tracking-wider text-gray-300 ${elementClassName}`}
    >
      {title}
    </div>
    <div
      className={`col-start-5 mb-1 mr-1 flex items-center justify-center ${elementClassName}`}
    >
      {icon}
    </div>
    <div className={`col-start-6 ${elementClassName}`}>
      <NameTag player={awayPlayer} />
    </div>
    <div className={`col-start-7 ${elementClassName}`}>
      <Avatar player={awayPlayer} />
    </div>
  </>
);

const BottomCenter = ({ gameData }) => {
  const homeCaptain = useMemo(
    () =>
      gameData?.Lineup?.[gameData?.HomeTeam?.short]?.find((p) => p.isCaptain),
    [gameData?.Lineup, gameData?.HomeTeam?.short],
  );
  const awayCaptain = useMemo(
    () =>
      gameData?.Lineup?.[gameData?.AwayTeam?.short]?.find((p) => p.isCaptain),
    [gameData?.Lineup, gameData?.AwayTeam?.short],
  );
  const homeGoalie = useMemo(
    () =>
      gameData?.Lineup?.[gameData?.HomeTeam?.short]?.find(
        (p) => p.ListPosition?.isGoalie,
      ),
    [gameData?.Lineup, gameData?.HomeTeam?.short],
  );
  const awayGoalie = useMemo(
    () =>
      gameData?.Lineup?.[gameData?.AwayTeam?.short]?.find(
        (p) => p.ListPosition?.isGoalie,
      ),
    [gameData?.Lineup, gameData?.AwayTeam?.short],
  );

  if (!gameData) return null;

  return (
    <div className="px-8 py-0.5 text-xl uppercase text-white">
      {/* 7-column grid: [L-Avatar][L-Name][Icon][Label][Icon][R-Name][R-Avatar] */}
      <div className="grid grid-cols-[auto_auto_auto_auto_auto_auto_auto] gap-x-2 gap-y-3 pt-1 text-center">
        {/* Row 1: CAPTAINS */}
        <SpecialPlayerRow
          title="CAPTAINS"
          homePlayer={homeCaptain}
          awayPlayer={awayCaptain}
          icon={<CrownIcon />}
          elementClassName="row-start-1"
        />
        {/* Row 2: GOALIES */}
        <SpecialPlayerRow
          title="GOALIES"
          homePlayer={homeGoalie}
          awayPlayer={awayGoalie}
          icon={<GoalieIcon />}
          elementClassName="row-start-2"
        />
      </div>
    </div>
  );
};

export default BottomCenter;
